import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Generate or retrieve a stable session_id from localStorage
function getSessionId(): string {
  const key = 'sin_session_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

const EDGE_FN_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-redirect`;

export default function PartnerRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [notFound, setNotFound] = useState(false);

  const isNotFound = searchParams.get('not_found') === '1';

  useEffect(() => {
    if (isNotFound || !slug) {
      setNotFound(true);
      return;
    }

    // Build redirect URL — all tracking params included so edge fn can log them
    const params = new URLSearchParams({
      slug,
      utm_source:   searchParams.get('utm_source')   ?? 'gift_store',
      utm_medium:   searchParams.get('utm_medium')   ?? 'affiliate',
      utm_campaign: searchParams.get('utm_campaign') ?? 'market',
      utm_content:  searchParams.get('utm_content')  ?? '',
      placement:    searchParams.get('placement')    ?? 'gift_store',
      locale:       navigator.language ?? 'en',
      session_id:   getSessionId(),
    });

    const ref = searchParams.get('ref');
    if (ref) params.set('ref', ref);

    const redirectUrl = `${EDGE_FN_BASE}?${params.toString()}`;

    // Open the edge function URL in this tab — the edge fn returns a 302
    // which the browser follows directly to the affiliate site.
    window.location.href = redirectUrl;
  }, [slug, isNotFound, searchParams]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-sm w-full text-center"
        >
          <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="h-7 w-7 text-destructive" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">
            That partner link is no longer available.
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            The partner may have moved or the link has expired. Browse our current partners below.
          </p>
          <Link to="/gift-store">
            <Button variant="default" className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Back to Gift Store
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  // Loading state while redirect is in-flight
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center gap-3 text-center"
      >
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Taking you to the partner…</p>
      </motion.div>
    </div>
  );
}
